import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { useToastViewportHost } from "./toast-viewport-host";
import { cn } from "../../lib/cn";
import { Button } from "./button";

type ToastTone = "info" | "success" | "warning" | "danger";

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  durationMs?: number | null;
}

interface ToastRecord extends ToastOptions {
  durationMs: number | null;
  id: number;
  tone: ToastTone;
}

interface ToastContextValue {
  notify: (options: ToastOptions) => void;
  dismiss: (id: number) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let toastSequence = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const viewportHost = useToastViewportHost();

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback(
    ({ title, description, tone = "info", durationMs }: ToastOptions) => {
      const id = ++toastSequence;
      const requestedDuration = durationMs === undefined
        ? tone === "danger"
          ? null
          : 10_000
        : durationMs;
      const resolvedDuration = requestedDuration !== null && Number.isFinite(requestedDuration) && requestedDuration > 0
        ? Math.min(requestedDuration, 2_147_483_647)
        : null;

      // A later notification must never dismiss an unresolved persistent message.
      setToasts((current) => [
        ...current,
        {
          id,
          title,
          description,
          durationMs: resolvedDuration,
          tone
        }
      ]);
    },
    []
  );

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const value = useMemo(() => ({ notify, dismiss, dismissAll }), [dismiss, dismissAll, notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {viewportHost ? createPortal(<ToastViewport toasts={toasts} onDismiss={dismiss} />, viewportHost) : null}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used inside ToastProvider.");
  }
  return context;
}

function ToastViewport({
  toasts,
  onDismiss
}: {
  toasts: ToastRecord[];
  onDismiss: (id: number) => void;
}) {
  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="ui-toast-viewport" role="region" aria-label="Status messages">
      {toasts.map((toast) => (
        <ToastMessage key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastMessage({ toast, onDismiss }: { toast: ToastRecord; onDismiss: (id: number) => void }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pageHidden, setPageHidden] = useState(() => typeof document !== "undefined" && document.hidden);
  const remainingMs = useRef(toast.durationMs);

  useEffect(() => {
    const updateVisibility = () => setPageHidden(document.hidden);
    updateVisibility();
    document.addEventListener("visibilitychange", updateVisibility);
    return () => document.removeEventListener("visibilitychange", updateVisibility);
  }, []);

  useEffect(() => {
    if (remainingMs.current === null || hovered || focused || pageHidden) return;

    const startedAt = window.performance.now();
    const timeoutID = window.setTimeout(() => onDismiss(toast.id), remainingMs.current);
    return () => {
      window.clearTimeout(timeoutID);
      if (remainingMs.current !== null) {
        remainingMs.current = Math.max(0, remainingMs.current - (window.performance.now() - startedAt));
      }
    };
  }, [focused, hovered, onDismiss, pageHidden, toast.id]);

  return (
    <div
      className={cn("ui-toast", `ui-toast-${toast.tone}`)}
      role={toast.tone === "danger" ? "alert" : "status"}
      aria-atomic="true"
      data-persistent={toast.durationMs === null || undefined}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setFocused(false);
        }
      }}
    >
      <span className="ui-toast-icon" aria-hidden="true">
        <ToastIcon tone={toast.tone} />
      </span>
      <span className="ui-toast-copy">
        <strong>{toast.title}</strong>
        {toast.description && <small>{toast.description}</small>}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="ui-toast-close"
        type="button"
        aria-label={`Dismiss message: ${toast.title}`}
        onClick={() => onDismiss(toast.id)}
      >
        <X size={16} />
      </Button>
    </div>
  );
}

function ToastIcon({ tone }: { tone: ToastTone }) {
  if (tone === "success") {
    return <CheckCircle2 size={18} />;
  }
  if (tone === "warning" || tone === "danger") {
    return <AlertTriangle size={18} />;
  }
  return <Info size={18} />;
}
